import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MagnifyingGlass, Info, ArrowLeft, ArrowRight,
  UploadSimple, GearSix, X, Warning, CheckCircle,
  FloppyDisk, CaretDown, CaretUp, Tag, Database,
  ClockCounterClockwise,
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ATTRIBUTION =
  'Fonte: Base de Dados da Composição de Alimentos. Instituto Nacional de Saúde Doutor Ricardo Jorge, I. P. INSA. v 7.1 - 2026';

const LABEL_STYLE = {
  'Rico em ferro':       'bg-red-100 text-red-700 border-red-200',
  'Fonte de ferro':      'bg-red-50 text-red-600 border-red-100',
  'Rico em zinco':       'bg-purple-100 text-purple-700 border-purple-200',
  'Fonte de zinco':      'bg-purple-50 text-purple-600 border-purple-100',
  'Rico em cálcio':      'bg-blue-100 text-blue-700 border-blue-200',
  'Fonte de cálcio':     'bg-blue-50 text-blue-600 border-blue-100',
  'Rico em vitamina D':  'bg-amber-100 text-amber-700 border-amber-200',
  'Fonte de vitamina D': 'bg-amber-50 text-amber-600 border-amber-100',
  'Rico em folatos':     'bg-green-100 text-green-700 border-green-200',
  'Fonte de folatos':    'bg-green-50 text-green-600 border-green-100',
  'Rico em vitamina B12':'bg-teal-100 text-teal-700 border-teal-200',
  'Fonte de vitamina B12':'bg-teal-50 text-teal-600 border-teal-100',
  'Rico em vitamina A':  'bg-orange-100 text-orange-700 border-orange-200',
  'Fonte de vitamina A': 'bg-orange-50 text-orange-600 border-orange-100',
  'Rico em vitamina C':  'bg-lime-100 text-lime-700 border-lime-200',
  'Fonte de vitamina C': 'bg-lime-50 text-lime-600 border-lime-100',
  'Rico em fibra':       'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Fonte de fibra':      'bg-emerald-50 text-emerald-600 border-emerald-100',
  'Baixo teor de sódio': 'bg-sky-100 text-sky-700 border-sky-200',
};

function LabelBadge({ label }) {
  const cls = LABEL_STYLE[label] || 'bg-brand-bg text-brand-muted border-brand-border';
  return (
    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${cls} whitespace-nowrap`}>
      {label}
    </span>
  );
}

function AttributionBanner({ version }) {
  const text = version
    ? `Fonte: Base de Dados da Composição de Alimentos. Instituto Nacional de Saúde Doutor Ricardo Jorge, I. P. INSA. v ${version} - 2026`
    : ATTRIBUTION;
  return (
    <div
      className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-5"
      data-testid="insa-attribution-banner"
    >
      <Info size={15} className="text-blue-500 mt-0.5 flex-shrink-0" weight="fill" />
      <p className="text-xs text-blue-800 leading-relaxed">{text}</p>
    </div>
  );
}

/* ─── Detail Modal ─────────────────────────────────────────── */
function FoodDetailModal({ food, onClose }) {
  if (!food) return null;
  const colMeta = food.column_metadata || [];
  const metaByKey = {};
  colMeta.forEach(m => { metaByKey[m.key] = m; });

  const componentes = food.componentes || {};
  const compEntries = Object.entries(componentes);
  const attribution = food.attribution || ATTRIBUTION;

  return (
    <Dialog open={!!food} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent
        className="rounded-3xl border-brand-border max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="food-detail-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-brand-text text-base">
            {food.nome}
          </DialogTitle>
          <DialogDescription className="text-xs text-brand-muted">
            Cód. {food.cod} · {food.nivel1}
            {food.nivel2 ? ` › ${food.nivel2}` : ''}
          </DialogDescription>
        </DialogHeader>

        {(food.etiquetas || []).length > 0 && (
          <div className="flex flex-wrap gap-1 pb-3 border-b border-brand-border">
            {food.etiquetas.map(l => <LabelBadge key={l} label={l} />)}
          </div>
        )}

        <div className="mt-2">
          <p className="text-[10px] font-semibold text-brand-muted uppercase tracking-wider mb-2">
            Composição por 100 g de parte edível
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
            {compEntries.map(([key, val]) => {
              const meta = metaByKey[key];
              const label = meta
                ? `${meta.display_name}${meta.unit ? ` (${meta.unit})` : ''}`
                : key;
              return (
                <div
                  key={key}
                  className="flex justify-between items-baseline py-1 border-b border-brand-border/40"
                  data-testid={`comp-row-${key}`}
                >
                  <span className="text-[11px] text-brand-muted truncate pr-2 max-w-[65%]">{label}</span>
                  <span className={`text-[11px] font-medium ${val === null ? 'text-brand-placeholder italic' : 'text-brand-text'}`}>
                    {val === null ? 'sem dado' : val}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-brand-border/60">
          <p className="text-[10px] text-brand-placeholder leading-relaxed">{attribution}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Settings Modal ───────────────────────────────────────── */
function SettingsModal({ settings, onClose, onSaved }) {
  const [thresholds, setThresholds] = useState(() =>
    JSON.parse(JSON.stringify(settings?.label_thresholds || {}))
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/insa-foods/settings`, { label_thresholds: thresholds });
      toast.success('Limiares guardados e etiquetas recalculadas');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, field, val) => {
    setThresholds(prev => ({ ...prev, [key]: { ...prev[key], [field]: parseFloat(val) || 0 } }));
  };

  const nutrientRows = [
    { key: 'ferro',        label: 'Ferro',        type: 'pct' },
    { key: 'zinco',        label: 'Zinco',        type: 'pct' },
    { key: 'calcio',       label: 'Cálcio',       type: 'pct' },
    { key: 'vitamina_d',   label: 'Vitamina D',   type: 'pct' },
    { key: 'folatos',      label: 'Folatos',      type: 'pct' },
    { key: 'vitamina_b12', label: 'Vitamina B12', type: 'pct' },
    { key: 'vitamina_a',   label: 'Vitamina A',   type: 'pct' },
    { key: 'vitamina_c',   label: 'Vitamina C',   type: 'pct' },
  ];

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="rounded-3xl border-brand-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="insa-settings-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-brand-text">Limiares das etiquetas</DialogTitle>
          <DialogDescription className="text-xs text-brand-muted">
            Ao guardar, todas as etiquetas são recalculadas automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Micronutrientes (Fonte de / Rico em) */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-muted mb-2">
              Micronutrientes — "Fonte de" (≥ X%) e "Rico em" (≥ Y%)
            </p>
            <div className="rounded-xl border border-brand-border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-brand-bg">
                  <tr>
                    <th className="text-left px-3 py-2 text-brand-muted font-medium">Nutriente</th>
                    <th className="text-right px-3 py-2 text-brand-muted font-medium">VRN (unidade)</th>
                    <th className="text-right px-3 py-2 text-brand-muted font-medium">Fonte de (%)</th>
                    <th className="text-right px-3 py-2 text-brand-muted font-medium">Rico em (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {nutrientRows.map(r => {
                    const t = thresholds[r.key] || {};
                    return (
                      <tr key={r.key} className="border-t border-brand-border/50">
                        <td className="px-3 py-1.5 text-brand-text">{r.label}</td>
                        <td className="px-3 py-1.5 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Input
                              type="number" step="0.1" min="0"
                              value={t.ref_value ?? ''}
                              onChange={e => setField(r.key, 'ref_value', e.target.value)}
                              className="w-20 h-7 text-xs rounded-lg border-brand-border text-right"
                              data-testid={`threshold-ref-${r.key}`}
                            />
                            <span className="text-brand-placeholder text-[10px] w-7">{t.unit}</span>
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <Input
                            type="number" step="1" min="0" max="100"
                            value={t.source_pct ?? ''}
                            onChange={e => setField(r.key, 'source_pct', e.target.value)}
                            className="w-16 h-7 text-xs rounded-lg border-brand-border text-right ml-auto"
                            data-testid={`threshold-source-${r.key}`}
                          />
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <Input
                            type="number" step="1" min="0" max="100"
                            value={t.rich_pct ?? ''}
                            onChange={e => setField(r.key, 'rich_pct', e.target.value)}
                            className="w-16 h-7 text-xs rounded-lg border-brand-border text-right ml-auto"
                            data-testid={`threshold-rich-${r.key}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Fibra */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Fibra — "Fonte de fibra" (≥ X g)</Label>
              <Input
                type="number" step="0.5" min="0"
                value={thresholds.fibra?.source_g ?? ''}
                onChange={e => setField('fibra', 'source_g', e.target.value)}
                className="rounded-xl border-brand-border h-9"
                data-testid="threshold-fibra-source"
              />
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Fibra — "Rico em fibra" (≥ X g)</Label>
              <Input
                type="number" step="0.5" min="0"
                value={thresholds.fibra?.rich_g ?? ''}
                onChange={e => setField('fibra', 'rich_g', e.target.value)}
                className="rounded-xl border-brand-border h-9"
                data-testid="threshold-fibra-rich"
              />
            </div>
          </div>

          {/* Sódio */}
          <div className="max-w-xs">
            <Label className="text-xs text-brand-muted mb-1 block">Sódio — "Baixo teor de sódio" (≤ X mg)</Label>
            <Input
              type="number" step="1" min="0"
              value={thresholds.sodio?.low_mg ?? ''}
              onChange={e => setField('sodio', 'low_mg', e.target.value)}
              className="rounded-xl border-brand-border h-9"
              data-testid="threshold-sodio-low"
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-brand-green text-white rounded-full py-3 font-medium text-sm disabled:opacity-50"
            data-testid="save-settings-btn"
          >
            {saving ? 'A recalcular...' : 'Guardar e recalcular etiquetas'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Import Modal ─────────────────────────────────────────── */
function ImportModal({ onClose, onImported }) {
  const [step, setStep] = useState('pick'); // 'pick' | 'preview' | 'done'
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/api/admin/insa-foods/import/preview`, fd);
      setPreview(data);
      setStep('preview');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao processar ficheiro');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview?.pending_id) return;
    setConfirming(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/insa-foods/import/confirm`, {
        pending_id: preview.pending_id,
      });
      toast.success(`${data.imported} alimentos importados (v${data.version})`);
      setStep('done');
      onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao confirmar importação');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="rounded-3xl border-brand-border max-w-lg" data-testid="import-insa-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-brand-text">Importar nova versão INSA</DialogTitle>
          <DialogDescription className="text-xs text-brand-muted">
            Substitui todos os dados INSA actuais pelos do ficheiro. O histórico da versão anterior é registado.
          </DialogDescription>
        </DialogHeader>

        {step === 'pick' && (
          <div className="space-y-4">
            <label className="block cursor-pointer">
              <div
                className={`border-2 border-dashed rounded-2xl p-8 text-center transition-colors ${
                  file ? 'border-brand-green bg-brand-green/5' : 'border-brand-border hover:border-brand-green/40'
                }`}
                data-testid="insa-file-drop"
              >
                <UploadSimple size={28} className={`mx-auto mb-2 ${file ? 'text-brand-green' : 'text-brand-placeholder'}`} weight="duotone" />
                <p className="text-sm text-brand-text font-medium">
                  {file ? file.name : 'Escolher ficheiro Excel (.xlsx)'}
                </p>
                {file && (
                  <p className="text-xs text-brand-muted mt-1">{(file.size / 1024).toFixed(0)} KB</p>
                )}
              </div>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="insa-file-input"
              />
            </label>
            <button
              onClick={handlePreview}
              disabled={!file || loading}
              className="w-full bg-brand-green text-white rounded-full py-3 font-medium text-sm disabled:opacity-50"
              data-testid="insa-preview-btn"
            >
              {loading ? 'A processar ficheiro...' : 'Pré-visualizar alterações'}
            </button>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-4">
            <div className="bg-brand-bg rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-brand-text">Versão detectada</span>
                <span className="text-sm font-bold text-brand-green">v{preview.version}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-brand-muted">Total de alimentos</span>
                <span className="text-sm font-medium text-brand-text">{preview.total_new}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center bg-green-50 border border-green-200 rounded-xl p-2">
                  <p className="text-lg font-bold text-green-700">{preview.added}</p>
                  <p className="text-[9px] text-green-600">Novos</p>
                </div>
                <div className="text-center bg-amber-50 border border-amber-200 rounded-xl p-2">
                  <p className="text-lg font-bold text-amber-700">{preview.changed}</p>
                  <p className="text-[9px] text-amber-600">Alterados</p>
                </div>
                <div className="text-center bg-red-50 border border-red-200 rounded-xl p-2">
                  <p className="text-lg font-bold text-red-700">{preview.removed}</p>
                  <p className="text-[9px] text-red-600">Removidos</p>
                </div>
              </div>
              {preview.nutrient_mappings_found?.length > 0 && (
                <p className="text-[10px] text-brand-muted">
                  Nutrientes detetados para etiquetas: {preview.nutrient_mappings_found.join(', ')}
                </p>
              )}
            </div>

            {preview.added_sample?.length > 0 && (
              <div>
                <p className="text-xs text-brand-muted mb-1">Exemplos de novos:</p>
                <div className="flex flex-wrap gap-1">
                  {preview.added_sample.map(c => (
                    <span key={c} className="text-[10px] bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full">{c}</span>
                  ))}
                </div>
              </div>
            )}

            {preview.removed_sample?.length > 0 && (
              <div>
                <p className="text-xs text-brand-muted mb-1">Exemplos de removidos:</p>
                <div className="flex flex-wrap gap-1">
                  {preview.removed_sample.map(c => (
                    <span key={c} className="text-[10px] bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-full">{c}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
              <Warning size={14} className="text-amber-600 mt-0.5 flex-shrink-0" weight="fill" />
              <p className="text-xs text-amber-800">
                Tem a certeza? Esta ação substitui <strong>todos os {preview.total_new} alimentos</strong> INSA actuais pelos do novo ficheiro. O histórico da versão anterior fica registado.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setStep('pick')}
                className="flex-1 border border-brand-border text-brand-muted rounded-full py-2.5 text-sm"
                data-testid="insa-back-btn"
              >
                Voltar
              </button>
              <button
                onClick={handleConfirm}
                disabled={confirming}
                className="flex-1 bg-brand-green text-white rounded-full py-2.5 text-sm font-medium disabled:opacity-50"
                data-testid="insa-confirm-import-btn"
              >
                {confirming ? 'A importar...' : 'Confirmar importação'}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="text-center py-6">
            <CheckCircle size={48} className="mx-auto text-brand-green mb-3" weight="duotone" />
            <p className="text-brand-text font-medium">Importação concluída</p>
            <p className="text-xs text-brand-muted mt-1">{ATTRIBUTION}</p>
            <button
              onClick={onClose}
              className="mt-4 bg-brand-green text-white rounded-full px-6 py-2.5 text-sm font-medium"
              data-testid="insa-done-btn"
            >
              Fechar
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Main Page ────────────────────────────────────────────── */
export default function TabelaINSAPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [foods, setFoods] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);
  const [selectedNivel1, setSelectedNivel1] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [groups, setGroups] = useState([]);
  const [allLabels, setAllLabels] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedFood, setSelectedFood] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [empty, setEmpty] = useState(false);
  const LIMIT = 50;

  const fetchMeta = useCallback(async () => {
    try {
      const [gRes, lRes, sRes] = await Promise.all([
        axios.get(`${API}/api/admin/insa-foods/groups`),
        axios.get(`${API}/api/admin/insa-foods/all-labels`),
        axios.get(`${API}/api/admin/insa-foods/settings`),
      ]);
      setGroups(gRes.data || []);
      setAllLabels(lRes.data || []);
      setSettings(sRes.data || {});
      setEmpty(!sRes.data?.total_foods);
    } catch { /* ignore */ }
  }, []);

  const fetchFoods = useCallback(async (p = 1, s = search, n1 = selectedNivel1, lbl = selectedLabel) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: p, limit: LIMIT,
        ...(s && { search: s }),
        ...(n1 && { nivel1: n1 }),
        ...(lbl && { etiqueta: lbl }),
      });
      const { data } = await axios.get(`${API}/api/admin/insa-foods?${params}`);
      setFoods(data.foods || []);
      setTotal(data.total || 0);
    } catch { toast.error('Erro ao carregar alimentos'); }
    finally { setLoading(false); }
  }, [search, selectedNivel1, selectedLabel]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  useEffect(() => {
    setPage(1);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchFoods(1, search, selectedNivel1, selectedLabel), 300);
    return () => clearTimeout(searchTimer.current);
  }, [search, selectedNivel1, selectedLabel, fetchFoods]);

  const openDetail = async (food) => {
    try {
      const { data } = await axios.get(`${API}/api/admin/insa-foods/${food.id || food.cod}`);
      setSelectedFood(data);
    } catch { toast.error('Erro ao carregar ficha'); }
  };

  const goPage = (p) => {
    setPage(p);
    fetchFoods(p);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="fade-in" data-testid="tabela-insa-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-medium text-xl text-brand-text">Tabela INSA</h2>
          <p className="text-xs text-brand-muted mt-1">
            {settings?.total_foods
              ? `${settings.total_foods} alimentos · v${settings.version}`
              : 'Base de Dados da Composição de Alimentos'}
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowSettings(true)}
              className="border border-brand-border text-brand-muted rounded-full px-3 py-2 text-xs flex items-center gap-1.5 hover:bg-brand-bg"
              data-testid="insa-settings-btn"
            >
              <GearSix size={14} /> Limiares
            </button>
            <button
              onClick={() => setShowImport(true)}
              className="bg-brand-green text-white rounded-full px-4 py-2 text-xs font-medium flex items-center gap-1.5"
              data-testid="insa-import-btn"
            >
              <UploadSimple size={14} /> Importar nova versão
            </button>
          </div>
        )}
      </div>

      {/* Attribution */}
      <AttributionBanner version={settings?.version} />

      {/* Empty state */}
      {empty && (
        <div className="text-center py-16 bg-white border border-brand-border rounded-2xl" data-testid="insa-empty-state">
          <Database size={48} className="mx-auto text-brand-placeholder mb-3" weight="duotone" />
          <p className="text-brand-text font-medium">Tabela INSA não importada</p>
          <p className="text-sm text-brand-muted mt-1 mb-4">
            Importa o ficheiro Excel BDCA do INSA para começar.
          </p>
          {isAdmin && (
            <button
              onClick={() => setShowImport(true)}
              className="bg-brand-green text-white rounded-full px-6 py-2.5 text-sm font-medium inline-flex items-center gap-2"
              data-testid="insa-empty-import-btn"
            >
              <UploadSimple size={16} /> Importar BDCA
            </button>
          )}
        </div>
      )}

      {!empty && (
        <>
          {/* Search + filters */}
          <div className="space-y-3 mb-4">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-placeholder" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Pesquisar por nome ou código..."
                className="pl-9 rounded-xl border-brand-border"
                data-testid="insa-search"
              />
            </div>

            {/* Group filter */}
            {groups.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="insa-group-filter">
                <button
                  onClick={() => setSelectedNivel1('')}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!selectedNivel1 ? 'bg-brand-green text-white' : 'bg-white text-brand-muted border border-brand-border hover:border-brand-green/40'}`}
                  data-testid="group-filter-all"
                >
                  Todos os grupos
                </button>
                {groups.map(g => (
                  <button
                    key={g}
                    onClick={() => setSelectedNivel1(selectedNivel1 === g ? '' : g)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors truncate max-w-[200px] ${selectedNivel1 === g ? 'bg-brand-green text-white' : 'bg-white text-brand-muted border border-brand-border hover:border-brand-green/40'}`}
                    data-testid={`group-filter-${g}`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            )}

            {/* Label filter */}
            {allLabels.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="insa-label-filter">
                <button
                  onClick={() => setSelectedLabel('')}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${!selectedLabel ? 'bg-brand-green/10 text-brand-green border border-brand-green/30' : 'bg-white text-brand-muted border border-brand-border'}`}
                  data-testid="label-filter-all"
                >
                  <Tag size={10} /> Todas as etiquetas
                </button>
                {allLabels.map(l => {
                  const cls = LABEL_STYLE[l] || 'bg-brand-bg text-brand-muted border-brand-border';
                  return (
                    <button
                      key={l}
                      onClick={() => setSelectedLabel(selectedLabel === l ? '' : l)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition-opacity ${cls} ${selectedLabel === l ? 'ring-2 ring-offset-1 ring-brand-green' : 'opacity-80 hover:opacity-100'}`}
                      data-testid={`label-filter-${l}`}
                    >
                      {l}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Results count */}
          {!loading && (
            <p className="text-xs text-brand-muted mb-3" data-testid="insa-total-count">
              {total} alimento{total !== 1 ? 's' : ''}
              {selectedNivel1 ? ` em "${selectedNivel1}"` : ''}
              {selectedLabel ? ` com etiqueta "${selectedLabel}"` : ''}
              {search ? ` para "${search}"` : ''}
            </p>
          )}

          {/* Food list */}
          {loading ? (
            <div className="text-center py-12 text-brand-muted text-sm">A carregar...</div>
          ) : foods.length === 0 ? (
            <div className="text-center py-12 text-brand-muted">
              <MagnifyingGlass size={36} className="mx-auto mb-2 text-brand-placeholder" weight="duotone" />
              <p className="text-sm">Sem resultados para os filtros actuais.</p>
            </div>
          ) : (
            <div className="space-y-1" data-testid="insa-food-list">
              {foods.map(food => (
                <button
                  key={food.id || food.cod}
                  onClick={() => openDetail(food)}
                  className="w-full text-left bg-white border border-brand-border rounded-xl px-4 py-3 hover:border-brand-green/40 hover:bg-brand-green/2 transition-colors group"
                  data-testid={`insa-food-${food.cod}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-[9px] text-brand-placeholder font-mono">{food.cod}</span>
                        <span className="text-sm font-medium text-brand-text truncate">{food.nome}</span>
                      </div>
                      {food.nivel1 && (
                        <p className="text-[10px] text-brand-muted mt-0.5">{food.nivel1}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end max-w-[45%]">
                      {(food.etiquetas || []).slice(0, 3).map(l => (
                        <LabelBadge key={l} label={l} />
                      ))}
                      {(food.etiquetas || []).length > 3 && (
                        <span className="text-[9px] text-brand-placeholder">+{food.etiquetas.length - 3}</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-5" data-testid="insa-pagination">
              <button
                onClick={() => goPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-brand-muted border border-brand-border disabled:opacity-40 hover:bg-brand-bg"
                data-testid="insa-prev-page"
              >
                <ArrowLeft size={12} /> Anterior
              </button>
              <span className="text-xs text-brand-muted">
                Pág. {page} / {totalPages} · {total} alimentos
              </span>
              <button
                onClick={() => goPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-brand-muted border border-brand-border disabled:opacity-40 hover:bg-brand-bg"
                data-testid="insa-next-page"
              >
                Próxima <ArrowRight size={12} />
              </button>
            </div>
          )}

          {/* Bottom attribution */}
          <div className="mt-6 border-t border-brand-border pt-4" data-testid="insa-bottom-attribution">
            <p className="text-[10px] text-brand-placeholder">{ATTRIBUTION}</p>
          </div>
        </>
      )}

      {/* Modals */}
      {selectedFood && (
        <FoodDetailModal food={selectedFood} onClose={() => setSelectedFood(null)} />
      )}
      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onImported={() => { fetchMeta(); fetchFoods(1); setEmpty(false); }}
        />
      )}
      {showSettings && settings && (
        <SettingsModal
          settings={settings}
          onClose={() => setShowSettings(false)}
          onSaved={() => { fetchMeta(); fetchFoods(1); }}
        />
      )}
    </div>
  );
}
